import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import { QuickCreateVendor, QuickCreateProduct } from '../../components/QuickCreateModals';
import ExportButton from '../../components/ExportButton';
import PDFExportButton from '../../components/PDFExportButton';

const MRN = () => {
  const [mrns, setMrns] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);

  const [isPoLinked, setIsPoLinked] = useState(true);
  const [formData, setFormData] = useState({
    is_po_linked: true, po_id: '', vendor_id: '',
    invoice_number: '', invoice_date: '', vehicle_number: '', notes: '', items: []
  });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [mrnsRes, posRes, vendRes, invRes] = await Promise.all([
        api.get('/purchase/mrn'), api.get('/purchase/orders?status=approved'),
        api.get('/purchase/vendors'), api.get('/sales/inventory')
      ]);
      setMrns(mrnsRes.data); setPurchaseOrders(posRes.data); setVendors(vendRes.data); setInventory(invRes.data);
    } catch (error) { console.error(error); } finally { setLoading(false); }
  };

  const openCreate = () => {
    setIsPoLinked(true);
    setFormData({ is_po_linked: true, po_id: '', vendor_id: '', invoice_number: '', invoice_date: '', vehicle_number: '', notes: '', items: [] });
    setShowModal(true);
  };

  const togglePoLinked = (linked) => {
    setIsPoLinked(linked);
    setFormData({ ...formData, is_po_linked: linked, po_id: '', vendor_id: '', items: [] });
  };

  const selectPO = (poId) => {
    const po = purchaseOrders.find(p => p.id === poId);
    if (po) {
      const items = po.items.map(item => ({
        sku: item.sku, name: item.name,
        ordered_quantity: item.quantity,
        received_quantity: item.quantity - (item.received_quantity || 0),
        accepted_quantity: item.quantity - (item.received_quantity || 0),
        rejected_quantity: 0, unit: item.unit, batch_number: '', remarks: ''
      }));
      setFormData({ ...formData, po_id: poId, vendor_id: po.vendor_id, items });
    }
  };

  const addManualItem = () => {
    setFormData({ ...formData, items: [...formData.items, { sku: '', name: '', ordered_quantity: 0, received_quantity: 0, accepted_quantity: 0, rejected_quantity: 0, unit: 'pcs', batch_number: '', remarks: '' }] });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'sku') {
      const inv = inventory.find(i => i.sku === value);
      if (inv) { newItems[index].name = inv.name; newItems[index].unit = inv.unit; }
    }
    if (field === 'rejected_quantity') newItems[index].accepted_quantity = newItems[index].received_quantity - value;
    if (field === 'accepted_quantity') newItems[index].rejected_quantity = newItems[index].received_quantity - value;
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) { alert('Add at least one item'); return; }
    try {
      await api.post('/purchase/mrn', formData);
      setShowModal(false); fetchData();
    } catch (error) { alert(error.response?.data?.detail || 'Failed to create MRN'); }
  };

  const filtered = mrns.filter(m =>
    (m.mrn_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.vendor_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.po_number || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="mrn-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Material Receipt Note (MRN)</h1>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/mrn" filename="mrn_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/mrn" filename="mrn_export.pdf" />
          <button onClick={openCreate} className="btn btn-primary" data-testid="create-mrn-btn"><Plus size={16} /> Create MRN</button>
        </div>
      </div>

      <div className="card mb-4">
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search MRN, PO, Vendor..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>

      <div className="card">
        {loading ? <div>Loading MRNs...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>MRN #</th><th>Type</th><th>PO #</th><th>Vendor</th><th>Items</th><th>Invoice #</th><th>Received Date</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No MRNs found</td></tr> : filtered.map((mrn) => (
                  <tr key={mrn.id} data-testid={`mrn-row-${mrn.mrn_number}`}>
                    <td className="font-mono">{mrn.mrn_number}</td>
                    <td>
                      {mrn.is_po_linked !== false ? (
                        <span className="badge badge-info">With PO</span>
                      ) : (
                        <span className="badge badge-warning">Without PO</span>
                      )}
                    </td>
                    <td className="font-mono">{mrn.po_number || '-'}</td>
                    <td>{mrn.vendor_name}</td>
                    <td>{mrn.items?.length || 0} items</td>
                    <td className="font-mono">{mrn.invoice_number || '-'}</td>
                    <td className="text-sm">{mrn.received_date ? new Date(mrn.received_date).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="mrn-modal">
          <div className="modal-content" style={{ maxWidth: '1000px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Create MRN</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* PO Toggle */}
                <div className="card mb-4" style={{ background: 'var(--bg-surface-alt)', padding: '12px 16px' }}>
                  <div className="flex items-center gap-4">
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>MRN Type:</span>
                    <button type="button" onClick={() => togglePoLinked(true)} className={`btn btn-sm ${isPoLinked ? 'btn-primary' : 'btn-secondary'}`} data-testid="toggle-with-po">
                      {isPoLinked ? <ToggleRight size={14} /> : <ToggleLeft size={14} />} With PO
                    </button>
                    <button type="button" onClick={() => togglePoLinked(false)} className={`btn btn-sm ${!isPoLinked ? 'btn-primary' : 'btn-secondary'}`} data-testid="toggle-without-po">
                      {!isPoLinked ? <ToggleRight size={14} /> : <ToggleLeft size={14} />} Without PO
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2">
                  {isPoLinked ? (
                    <div className="form-group">
                      <label className="form-label">Purchase Order *</label>
                      <select className="form-select" value={formData.po_id} onChange={(e) => selectPO(e.target.value)} required data-testid="po-select">
                        <option value="">Select PO...</option>
                        {purchaseOrders.map((po) => <option key={po.id} value={po.id}>{po.po_number} - {po.vendor_name}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label className="form-label">
                        Vendor *
                        <button type="button" onClick={() => setShowVendorModal(true)} className="btn btn-ghost btn-sm" style={{ padding: '0 4px', marginLeft: '4px' }}><Plus size={12} /></button>
                      </label>
                      <select className="form-select" value={formData.vendor_id} onChange={(e) => setFormData({...formData, vendor_id: e.target.value})} required data-testid="vendor-select">
                        <option value="">Select Vendor...</option>
                        {vendors.map(v => <option key={v.id} value={v.id}>{v.name} ({v.vendor_code})</option>)}
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Invoice Number</label>
                    <input type="text" className="form-input" value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })} data-testid="invoice-number-input" />
                  </div>
                </div>

                <div className="grid grid-cols-2">
                  <div className="form-group"><label className="form-label">Invoice Date</label><input type="date" className="form-input" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} data-testid="invoice-date-input" /></div>
                  <div className="form-group"><label className="form-label">Vehicle Number</label><input type="text" className="form-input" value={formData.vehicle_number} onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })} data-testid="vehicle-number-input" /></div>
                </div>

                {/* Items */}
                <div className="form-group">
                  <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
                    <label className="form-label" style={{ margin: 0 }}>Items</label>
                    {!isPoLinked && (
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setShowProductModal(true)} className="btn btn-ghost btn-sm" data-testid="quick-create-product-btn"><Plus size={12} /> New Product</button>
                        <button type="button" onClick={addManualItem} className="btn btn-secondary btn-sm" data-testid="add-item-btn"><Plus size={14} /> Add Item</button>
                      </div>
                    )}
                  </div>
                  {formData.items.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '4px' }}>SKU</th><th style={{ padding: '4px' }}>Name</th>
                            <th style={{ padding: '4px' }}>Ordered</th><th style={{ padding: '4px' }}>Received</th>
                            <th style={{ padding: '4px' }}>Accepted</th><th style={{ padding: '4px' }}>Rejected</th>
                            <th style={{ padding: '4px' }}>Batch #</th>
                            {!isPoLinked && <th style={{ padding: '4px' }}></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={index}>
                              <td style={{ padding: '4px' }}>
                                {isPoLinked ? <span className="font-mono">{item.sku}</span> : (
                                  <select className="form-select" value={item.sku} onChange={(e) => updateItem(index, 'sku', e.target.value)} style={{ width: '140px', fontSize: '12px', padding: '4px' }}>
                                    <option value="">Select...</option>
                                    {inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                                  </select>
                                )}
                              </td>
                              <td style={{ padding: '4px' }}>{item.name}</td>
                              <td style={{ padding: '4px' }} className="tabular-nums">{item.ordered_quantity}</td>
                              <td style={{ padding: '4px' }}><input type="number" className="form-input" value={item.received_quantity} onChange={(e) => updateItem(index, 'received_quantity', parseInt(e.target.value) || 0)} required style={{ width: '70px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="number" className="form-input" value={item.accepted_quantity} onChange={(e) => updateItem(index, 'accepted_quantity', parseInt(e.target.value) || 0)} required style={{ width: '70px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="number" className="form-input" value={item.rejected_quantity} onChange={(e) => updateItem(index, 'rejected_quantity', parseInt(e.target.value) || 0)} style={{ width: '70px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.batch_number} onChange={(e) => updateItem(index, 'batch_number', e.target.value)} style={{ width: '100px', fontSize: '12px', padding: '4px' }} /></td>
                              {!isPoLinked && <td style={{ padding: '4px' }}><button type="button" onClick={() => removeItem(index)} className="btn btn-ghost btn-sm" style={{ color: '#DC2626' }}>x</button></td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {formData.items.length === 0 && <p style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>{isPoLinked ? 'Select a PO to load items' : 'Add items manually'}</p>}
                </div>

                <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="notes-input" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary" data-testid="cancel-btn">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-mrn-btn">Create MRN</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showVendorModal && <QuickCreateVendor onCreated={(v) => { setVendors([...vendors, v]); setFormData({...formData, vendor_id: v.id}); }} onClose={() => setShowVendorModal(false)} />}
      {showProductModal && <QuickCreateProduct onCreated={(p) => { setInventory([...inventory, p]); }} onClose={() => setShowProductModal(false)} />}
    </div>
  );
};

export default MRN;
